import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";

const fields = [
  { name: "name",        label: "Full Name",       placeholder: "Your full name" },
  { name: "companyName", label: "Company",          placeholder: "Your company name" },
  { name: "number",      label: "Contact Number",   placeholder: "Your phone number" },
  { name: "email",       label: "Email Address",    placeholder: "your@email.com" },
  { name: "message",     label: "Message",          placeholder: "Describe your packaging requirements..." },
];

const Contact = () => {
  const form = useRef();
  const [state, setState] = useState({
    email: "", name: "", number: "", companyName: "", message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmit, setSumbit] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors(validate(state));
    setSumbit(true);
  };

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmit) {
      emailjs.sendForm(
        "service_yt2ohmd",
        "template_juenpkb",
        form.current,
        "XgpZsrhb-sJPakvFY"
      ).then(
        () => { alert("Successfully submitted"); window.location.href = "/"; },
        (err) => { console.log(err.text); alert("Failed to send. Please try again."); }
      );
    }
  }, [errors, isSubmit]);

  const validate = (v) => {
    const err = {};
    if (!v.name)        err.name        = "Required";
    if (!v.companyName) err.companyName = "Required";
    if (!v.email)       err.email       = "Required";
    if (!v.number)      err.number      = "Required";
    if (!v.message)     err.message     = "Required";
    return err;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageWrapper>
        {/* Left panel */}
        <LeftPanel>
          <PanelTag>Get in Touch</PanelTag>
          <PanelTitle>Let's talk packaging.</PanelTitle>
          <PanelDesc>
            Send us your requirements and we'll get back within 24&nbsp;hours
            with pricing and availability.
          </PanelDesc>

          <ContactBlock>
            <ContactRow>
              <ContactKey>Phone</ContactKey>
              <ContactVal>+91 9323582798</ContactVal>
            </ContactRow>
            <ContactRow>
              <ContactKey>Email</ContactKey>
              <ContactVal>brplastm@gmail.com</ContactVal>
            </ContactRow>
            <ContactRow>
              <ContactKey>Delivery</ContactKey>
              <ContactVal>Pan-India</ContactVal>
            </ContactRow>
          </ContactBlock>
        </LeftPanel>

        {/* Right panel — form */}
        <RightPanel>
          <FormCard ref={form} onSubmit={handleSubmit}>
            {fields.map((f) => (
              <FieldGroup key={f.name}>
                <FieldLabel htmlFor={f.name}>
                  {f.label}
                  {errors[f.name] && <FieldError> — {errors[f.name]}</FieldError>}
                </FieldLabel>
                <FieldInput
                  id={f.name}
                  name={f.name}
                  placeholder={f.placeholder}
                  value={state[f.name]}
                  onChange={handleInputChange}
                  hasError={!!errors[f.name]}
                />
              </FieldGroup>
            ))}
            <FormFooter>
              <SubmitBtn type="submit">Send Message &rarr;</SubmitBtn>
            </FormFooter>
          </FormCard>
        </RightPanel>
      </PageWrapper>
    </motion.div>
  );
};

const PageWrapper = styled.div`
  min-height: 100vh;
  padding-top: var(--navbar-h);
  display: grid;
  grid-template-columns: 1fr 1.4fr;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  padding: 72px 56px;
  background: var(--color-bg-subtle);
  border-right: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 900px) {
    padding: 48px 24px 40px;
    border-right: none;
    border-bottom: 1px solid var(--color-border);
  }
`;

const PanelTag = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: var(--color-muted);
  margin-bottom: 16px;
`;

const PanelTitle = styled.h1`
  font-size: 42px;
  font-weight: 800;
  color: var(--color-dark);
  margin: 0 0 16px;
  letter-spacing: -1.5px;
  line-height: 1.1;

  @media (max-width: 768px) { font-size: 32px; }
`;

const PanelDesc = styled.p`
  font-size: 15px;
  color: var(--color-muted);
  line-height: 1.75;
  margin: 0 0 48px;
  max-width: 340px;
`;

const ContactBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border-top: 1px solid var(--color-border);
`;

const ContactRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid var(--color-border);
`;

const ContactKey = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--color-muted);
  min-width: 100px;
`;

const ContactVal = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: var(--color-dark);
`;

const RightPanel = styled.div`
  padding: 72px 64px;
  background: var(--color-bg);
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media (max-width: 900px) {
    padding: 48px 24px;
  }
`;

const FormCard = styled.form`
  width: 100%;
  max-width: 520px;
`;

const FieldGroup = styled.div`
  margin-bottom: 28px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-dark);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
`;

const FieldError = styled.span`
  color: #dc2626;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 12px 0;
  box-sizing: border-box;
  background: none;
  outline: none;
  border: 0;
  border-bottom: 1.5px solid ${p => p.hasError ? '#dc2626' : 'var(--color-border)'};
  font-family: var(--font-primary);
  font-size: 15px;
  color: var(--color-text);
  transition: border-color 0.2s ease;

  &:focus {
    border-bottom-color: ${p => p.hasError ? '#dc2626' : 'var(--color-brand)'};
  }

  &::placeholder { color: #c4c4c4; }
`;

const FormFooter = styled.div`
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
  margin-top: 8px;
`;

const SubmitBtn = styled.button`
  background: var(--color-dark);
  color: white;
  border: none;
  padding: 15px 40px;
  border-radius: var(--radius-btn);
  font-size: 14px;
  font-weight: 700;
  font-family: var(--font-primary);
  cursor: pointer;
  transition: var(--transition);
  letter-spacing: 0.5px;

  &:hover {
    background: var(--color-brand);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(66,87,216,0.25);
  }
`;

export default Contact;
